import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useNavigate } from 'react-router-dom';
import { Zap, BookOpen, Music, CheckCircle, ArrowRight, GraduationCap, DollarSign, Briefcase, Heart } from 'lucide-react';
import CorporateHeader from './CorporateHeader';
import Footer from './Footer';

const PowerMovesPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/waitlist`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, source: 'power-moves' })
      });
      if (res.ok) setSubscribed(true);
    } catch {
      setSubscribed(true);
    } finally {
      setSubmitting(false);
    }
  };

  const curriculumModules = [
    { icon: DollarSign, title: 'Financial Literacy', desc: 'Budgeting, investing, credit building, and wealth creation fundamentals.' },
    { icon: Briefcase, title: 'Entrepreneurship', desc: 'Business planning, branding, marketing, and scaling your vision.' },
    { icon: GraduationCap, title: 'Career Development', desc: 'Resume building, networking, interview skills, and career mapping.' },
    { icon: Heart, title: 'Personal Development', desc: 'Mental health, communication, leadership, and decision-making.' },
  ];

  return (
    <div className="min-h-screen bg-black">
      <CorporateHeader />

      {/* Hero */}
      <section className="pt-28 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_var(--tw-gradient-stops))] from-amber-900/30 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-600/20 border border-amber-600/40 text-amber-400 text-sm font-semibold px-4 py-2 rounded-full mb-6">
                <Zap className="w-4 h-4" />
                COMING SOON
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4 leading-tight" data-testid="powermoves-title">
                Power Moves
              </h1>
              <p className="text-2xl font-bold text-amber-400 mb-6">
                The Life Skills We Were Never Taught
              </p>
              <p className="text-lg text-gray-400 leading-relaxed mb-8">
                A comprehensive curriculum system that teaches the real-world skills missing from traditional education — financial literacy, entrepreneurship, career development, and personal growth. Designed for students, young professionals, and anyone ready to make their next power move.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => document.getElementById('waitlist').scrollIntoView({ behavior: 'smooth' })} className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-8 py-3 text-lg">
                  Get Early Access
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button onClick={() => navigate('/start')} variant="outline" className="border-gray-700 text-gray-300 hover:border-amber-500 hover:text-amber-400">
                  Explore eCoSystem
                </Button>
              </div>
            </div>

            {/* Book Cover */}
            <div className="relative flex justify-center">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_eblack-store/artifacts/24w6xjnt_POWER%20MOVES%20front%20cover.png"
                  alt="Power Moves Book Cover"
                  className="w-80 lg:w-96 rounded-xl shadow-2xl shadow-amber-900/30"
                  data-testid="powermoves-cover"
                />
                <div className="absolute -bottom-4 -right-4 bg-amber-600 text-white text-sm font-bold px-4 py-2 rounded-full shadow-lg">
                  Full Curriculum System
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* All Covers Showcase */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">The Complete Collection</h2>
          <p className="text-gray-400 mb-10 max-w-2xl mx-auto">Multiple volumes covering every essential life skill — from financial literacy to entrepreneurship.</p>
          <img
            src="https://customer-assets.emergentagent.com/job_eblack-store/artifacts/coi3k4m5_POWER%20MOVES%20ALL%20COVERS.png"
            alt="Power Moves Complete Collection"
            className="max-w-4xl w-full mx-auto rounded-xl shadow-2xl"
            data-testid="powermoves-all-covers"
          />
        </div>
      </section>

      {/* Curriculum Modules */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-3">What You'll Learn</h2>
            <p className="text-gray-400 max-w-xl mx-auto">A complete life skills curriculum built for real-world application</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {curriculumModules.map((mod) => {
              const Icon = mod.icon;
              return (
                <div key={mod.title} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 text-center hover:border-amber-600/40 transition-all">
                  <div className="w-14 h-14 bg-amber-600/20 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-amber-400" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{mod.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{mod.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Future Soundtrack Placeholder */}
      <section className="py-16 bg-gradient-to-b from-black to-gray-900/50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto bg-gray-900/50 border border-amber-800/30 rounded-2xl p-8 lg:p-12 text-center">
            <Music className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-white mb-3">Power Moves Soundtrack</h3>
            <p className="text-gray-400 mb-4">
              An original soundtrack is being developed to accompany the Power Moves curriculum — motivational tracks to fuel your journey.
            </p>
            <span className="inline-block bg-gray-800 text-amber-400 text-sm font-semibold px-4 py-2 rounded-full">
              Coming Soon
            </span>
          </div>
        </div>
      </section>

      {/* Email Capture / Waitlist */}
      <section id="waitlist" className="py-20 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_var(--tw-gradient-stops))] from-amber-900/20 via-transparent to-transparent" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <Zap className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-3">Get Early Access</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Be the first to know when Power Moves launches. Join the waitlist for exclusive early access, updates, and special pricing.
          </p>

          {subscribed ? (
            <div className="flex items-center justify-center gap-2 text-green-400 font-semibold text-lg" data-testid="waitlist-subscribed">
              <CheckCircle className="w-6 h-6" />
              <span>You're on the list! We'll notify you when Power Moves launches.</span>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="max-w-lg mx-auto space-y-3">
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name (optional)"
                className="bg-black border-gray-700 text-white"
                data-testid="waitlist-name"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="bg-black border-gray-700 text-white flex-1"
                  data-testid="waitlist-email"
                />
                <Button type="submit" disabled={submitting} className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-8" data-testid="waitlist-submit">
                  {submitting ? 'Joining...' : 'Join Waitlist'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PowerMovesPage;
